import { z } from 'zod';
import { PublicProfileDto } from './networking';

export const CreateConversationInput = z.object({
  targetUserId: z.uuid(),
  /** Share short-code визитки: предъявление ссылки, которую владелец раздал сам. */
  via: z.string().min(1).max(16).optional(),
});
export type CreateConversationInput = z.infer<typeof CreateConversationInput>;

/** Знакомство с чужой визитки: владелец карточки и свой взаимный контакт. */
export const CreateIntroductionInput = z.object({
  ownerUserId: z.uuid(),
  contactUserId: z.uuid(),
  /** Share short-code визитки владельца — как у CreateConversationInput. */
  via: z.string().min(1).max(16).optional(),
});
export type CreateIntroductionInput = z.infer<typeof CreateIntroductionInput>;

export const SendMessageInput = z.object({
  clientMessageId: z.uuid(),
  text: z.string().min(1).max(4000),
});
export type SendMessageInput = z.infer<typeof SendMessageInput>;

export const SendPhotoCaptionInput = z.object({
  clientMessageId: z.uuid(),
  text: z.string().min(1).max(4000).optional(),
});
export type SendPhotoCaptionInput = z.infer<typeof SendPhotoCaptionInput>;

export const SendVideoCaptionInput = z.object({
  clientMessageId: z.uuid(),
  text: z.string().min(1).max(4000).optional(),
});
export type SendVideoCaptionInput = z.infer<typeof SendVideoCaptionInput>;

/** Переключение режима EVENT-канала (только организатор). */
export const UpdateConversationInput = z.object({
  announcementsOnly: z.boolean(),
});
export type UpdateConversationInput = z.infer<typeof UpdateConversationInput>;

export const MessagesQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export type MessagesQuery = z.infer<typeof MessagesQuery>;

/** Отправитель сообщения для группового рендера (EVENT-каналы). */
export const MessageSenderDto = z.object({
  userId: z.uuid(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  /** Организатор события EVENT-канала; в DIRECT всегда false. */
  isOrganizer: z.boolean(),
});
export type MessageSenderDto = z.infer<typeof MessageSenderDto>;

export const MessageDto = z.object({
  id: z.uuid(),
  conversationId: z.uuid(),
  senderUserId: z.uuid(),
  text: z.string().nullable(),
  clientMessageId: z.string(),
  createdAt: z.iso.datetime(),
  photoUrl: z.url().nullable(),
  photoWidth: z.number().int().nullable(),
  photoHeight: z.number().int().nullable(),
  videoUrl: z.url().nullable(),
  videoThumbnailUrl: z.url().nullable(),
  videoWidth: z.number().int().nullable(),
  videoHeight: z.number().int().nullable(),
  videoDurationSec: z.number().int().nullable(),
  videoStatus: z.enum(['PROCESSING', 'READY', 'FAILED', 'ARCHIVED']).nullable(),
  videoFailureReason: z.enum(['UNSUPPORTED_CODEC', 'TOO_LONG', 'CORRUPT']).nullable(),
  /** Кого представили этим сообщением. null у обычной переписки. */
  introducedUserId: z.uuid().nullable(),
  /** Нет у optimistic-сообщений клиента; сервер заполняет всегда. */
  sender: MessageSenderDto.optional(),
});
export type MessageDto = z.infer<typeof MessageDto>;

/** Краткая инфа о событии для EVENT-канала (обложка — presigned URL или внешний URL). */
export const ConversationEventInfo = z.object({
  id: z.uuid(),
  title: z.string(),
  coverUrl: z.string().nullable(),
});
export type ConversationEventInfo = z.infer<typeof ConversationEventInfo>;

/** Краткая инфа о клубе для общего чата. */
export const ConversationClubInfo = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  logoUrl: z.string().nullable(),
});
export type ConversationClubInfo = z.infer<typeof ConversationClubInfo>;

const conversationBase = {
  id: z.uuid(),
  lastMessageAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  /** false → поле ввода скрыто (читатель канала объявлений). */
  canWrite: z.boolean(),
};

export const PendingRsvpRequestRef = z.object({
  attendanceId: z.uuid(),
  eventId: z.uuid(),
  eventTitle: z.string(),
});
export type PendingRsvpRequestRef = z.infer<typeof PendingRsvpRequestRef>;

export const ConversationDto = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('direct'),
    peer: PublicProfileDto,
    pendingRsvpRequest: PendingRsvpRequestRef.optional(),
    peerBotBlocked: z.object({ telegramUsername: z.string().nullable() }).optional(),
    /**
     * Курсор прочтения собеседника (`ConversationParticipant.lastReadAt`).
     * Сообщение прочитано, если `createdAt <= peerLastReadAt`. null — peer ещё не markRead.
     */
    peerLastReadAt: z.iso.datetime().nullable(),
    ...conversationBase,
  }),
  z.object({
    kind: z.literal('event'),
    event: ConversationEventInfo,
    /** true — пишет только организатор (канал объявлений). */
    announcementsOnly: z.boolean(),
    /** Текущий пользователь — организатор события (рендер toggle режима). */
    isOrganizer: z.boolean(),
    ...conversationBase,
  }),
  z.object({
    kind: z.literal('club'),
    club: ConversationClubInfo,
    ...conversationBase,
  }),
]);
export type ConversationDto = z.infer<typeof ConversationDto>;

/** Payload WS/fanout `conversation.read` — live-синк галочек у отправителя. */
export const ConversationReadPayload = z.object({
  conversationId: z.uuid(),
  userId: z.uuid(),
  lastReadAt: z.iso.datetime(),
});
export type ConversationReadPayload = z.infer<typeof ConversationReadPayload>;

export const MessagesPage = z.object({
  items: z.array(MessageDto),
  nextCursor: z.string().nullable(),
});
export type MessagesPage = z.infer<typeof MessagesPage>;

export const UnreadItem = z.object({
  conversationId: z.uuid(),
  /** null для EVENT- и CLUB-каналов (нет собеседника). */
  peerUserId: z.uuid().nullable(),
  unread: z.number().int().nonnegative(),
});
export type UnreadItem = z.infer<typeof UnreadItem>;

export const UnreadSummary = z.array(UnreadItem);
export type UnreadSummary = z.infer<typeof UnreadSummary>;

const listItemBase = {
  id: z.uuid(),
  lastMessageAt: z.iso.datetime(),
  unread: z.number().int().nonnegative(),
};

export const ConversationListItem = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('direct'), peer: PublicProfileDto, ...listItemBase }),
  z.object({ kind: z.literal('event'), event: ConversationEventInfo, ...listItemBase }),
  z.object({ kind: z.literal('club'), club: ConversationClubInfo, ...listItemBase }),
]);
export type ConversationListItem = z.infer<typeof ConversationListItem>;

export const ConversationList = z.array(ConversationListItem);
export type ConversationList = z.infer<typeof ConversationList>;
